import { z } from "zod";

import { VibeAppVisibilitySchema } from "../../../../schemas/VibeApp";
import { VibeAppSchema } from "./core";

/**
 * An app's per-app edge-auth token — the shared secret the Traefik edge
 * matches (via the `X-Vibe-App-Token` header) before admitting a request to
 * the app. Minted + sealed at creation, revealed here on an authenticated
 * org-scoped read of the org's OWN app.
 *
 * `token` is a live secret: presenting it at the edge grants access to the
 * deployed app. Treat the whole payload as sensitive — the console reveals it
 * once behind an explicit action and never keeps it in a list response.
 */
export const VibeEdgeTokenSchema = z.object({
  /// The decrypted edge-auth token. SECRET — send it in the `X-Vibe-App-Token`
  /// header on every request to the app; never log or persist it client-side.
  token: z.string(),
  /// The HTTP header the edge matches the token against (`X-Vibe-App-Token`).
  /// Returned so the console can render a copy-paste `curl` without hardcoding
  /// the header name.
  headerName: z.string(),
  /// The app's canonical public URL, so the reveal payload is enough to build a
  /// working test request on its own. `null` only for transition-era rows that
  /// predate the canonical-URL system.
  publicUrl: z.string().nullable()
});

export type VibeEdgeTokenDto = z.infer<typeof VibeEdgeTokenSchema>;

// Reveal (GET) — id-scoped, no body. The path id addresses the app; the org
// scope comes from the JWT.
export const GetVibeEdgeTokenPathVarsSchema = z.object({
  id: z.string().uuid()
});

export const GetVibeEdgeTokenResponseSchema = z.object({
  edgeToken: VibeEdgeTokenSchema
});

// Rotate (POST) — mint a fresh token and swap it in atomically (build-then-
// swap: the new token is written before the old is forgotten), returning the
// new secret in the same reveal-once shape.
export const RotateVibeEdgeTokenPathVarsSchema = z.object({
  id: z.string().uuid()
});

export const RotateVibeEdgeTokenResponseSchema = z.object({
  edgeToken: VibeEdgeTokenSchema,
  /// `true` when the app is registered as an agent tool — rotating invalidates
  /// the token baked into that tool's auth, so the tool must be re-registered
  /// or it will 404 at the edge. The console surfaces this as a warning.
  toolResyncRequired: z.boolean()
});

// Set visibility (PATCH) — flips the app between PRIVATE (edge-auth token
// minted, header required at the edge) and PUBLIC (token dropped, bare
// `Host()` route, reachable by URL). The token and the visibility column move
// together in one write: the deploy path fails closed on a PRIVATE app with no
// token, so a torn write would strand the app unreachable.
export const SetVibeAppVisibilityPathVarsSchema = z.object({
  id: z.string().uuid()
});

export const SetVibeAppVisibilityBodySchema = z.object({
  /// The intent to store. `PUBLIC` drops the edge-auth token (anyone with the
  /// URL reaches the app); `PRIVATE` mints a fresh one and requires the header.
  visibility: VibeAppVisibilitySchema
});

export const SetVibeAppVisibilityResponseSchema = z.object({
  /// The app after the flip, with `visibility` reflecting the new intent.
  app: VibeAppSchema,
  /// The freshly minted secret — present ONLY when the app became `PRIVATE`
  /// (reveal-once, same shape as rotate). `null` when it became `PUBLIC`, which
  /// has no token by definition.
  edgeToken: VibeEdgeTokenSchema.nullable(),
  /// `true` when the app is a registered agent tool AND the flip invalidated the
  /// token that tool sends — i.e. only on `→ PRIVATE`, which mints a new secret
  /// the tool does not have yet. Going `→ PUBLIC` never sets this: the route
  /// becomes a bare `Host()` match, so a tool still sending the stale header
  /// keeps matching and keeps working.
  toolResyncRequired: z.boolean()
});
